import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import { useApplication } from '@eneco/common/application';
import logger from '@eneco/common/log';
import RichText from '@eneco/components/RichText/RichText';
import {
  $CompanyFormatTypeDto,
  deleteEnecoBeXapiSiteApiV1OrdersByOrderIdPromo,
  GenderDto,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
  getEnecoBeXapiSiteApiV1OrdersByOrderId,
  putEnecoBeXapiSiteApiV1OrdersByOrderIdPersonalAndCompanyDetails,
} from '@eneco/dc-be/client';
import type {
  GetOrderResponse,
  OrderCompanyDetailsDto,
  UpdatePersonalAndCompanyDetailsRequest,
} from '@eneco/dc-be/client/types.gen';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import usePublicDCBE from '@eneco/dc-be/hooks/usePublicDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { unwrapValidationError } from '@eneco/dc-be/utils/unwrapError';
import { useRouter } from '@eneco/next';
import {
  AlertDialog,
  Bucket,
  Button,
  Checkbox,
  Heading,
  InputEmail,
  InputSelect,
  InputText,
  NotificationBox,
  RadioGroup,
  RadioTile,
  Stack,
} from '@eneco/sparky';
import { Input } from '@eneco/sparky/components/Input/Input';
import { ProfileIcon } from '@eneco/sparky/icons';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { DialogField, Fields, OrderManagementRendering } from '@sitecore/types/OrderManagement';
import { format } from 'date-fns';
import { Controller, Path, useFormContext } from 'react-hook-form';
import { useSWRConfig } from 'swr';

import { CompanyNumberInput } from '../../../components/CompanyNumberInput';
import DataErrorNotification from '../../../components/DataErrorNotification';
import InfoComponent from '../../../components/InfoComponent';
import PhoneNumberInputField from '../../../components/PhoneNumberInputField';
import { languageToCustomerLanguageDto } from '../../../utils/language';
import { useOrderId } from '../hooks/useOrderId';
import { useSelectedProducts } from '../hooks/useSelectedProducts';
import { OrderManagementFormSchema } from '../types/orderManagementSchema';
import { FlowChildProps } from '../types/OrderManagementSteps';
import { genderToRepresentation, representationMr, representationToGender } from '../utils/gender';
import { getTrackableFormValues } from '../utils/getTrackableFormValues';
import {
  isCompanyNumberRequested,
  mapVatStatusToSubjectToVAT,
  resolveVatStatusFromCompanyDetails,
} from '../utils/vatStatus';

const PROFESSIONAL_USER_OPTION_YES = 'Yes';
const PROFESSIONAL_USER_OPTION_NO = 'No';

const buildCompanyDetails = (values: OrderManagementFormSchema['personalDetailsStep']): OrderCompanyDetailsDto => {
  const companyNumberRequested = isCompanyNumberRequested(values.vatStatus);
  const isSubjectToVat = mapVatStatusToSubjectToVAT(values.vatStatus);
  return {
    companyName: values.companyName ?? '',
    companyType: values.companyType ?? $CompanyFormatTypeDto.enum[0],
    vatNumber: companyNumberRequested ? null : (values.VATNumber ?? null),
    companyNumberIsBeingRequested: companyNumberRequested,
    subjectToVAT: mapVatStatusToSubjectToVAT(values.vatStatus),
    hasCoContracting: (values.isCocontractor && isSubjectToVat) ?? false,
  };
};

const emptyCompanyDetails: OrderCompanyDetailsDto = {
  companyName: '',
  vatNumber: null,
  companyType: $CompanyFormatTypeDto.enum[0],
  companyNumberIsBeingRequested: false,
  subjectToVAT: null,
  hasCoContracting: false,
};

const getPromoLossRiskMessage = (
  fields: Fields,
  customerIsSoho: boolean,
  hasEBillingPromoLossRisk: boolean,
  hasSohoPromoLossRisk: boolean,
): DialogField | null => {
  if (hasSohoPromoLossRisk && !customerIsSoho) return fields.personalDetailsStep.promoLossBecauseCustomerTypeDialog;
  if (hasSohoPromoLossRisk && customerIsSoho) return fields.personalDetailsStep.promoLossBecauseCustomerIsSohoDialog;
  if (hasEBillingPromoLossRisk) return fields.personalDetailsStep.promoLossBecauseEBillingDialog;

  return null;
};

export const PersonalDetailsStep: FC<FlowChildProps> = ({ nextMainStep }) => {
  const { fields } = useContent<OrderManagementRendering>();
  const { trackEventBE } = useTracking();
  const [isLoadingSubmit, setIsLoadingSubmit] = useState<boolean>(false);
  const [isVatRequired, setIsVatRequired] = useState<boolean>(false);
  const [showPromotionWarning, setShowPromotionWarning] = useState<boolean>(false);
  const { language } = useApplication();
  const { data: session } = useSession();
  const { selectedAccount } = useSelfServiceAccount();
  const { mutate } = useSWRConfig();
  const orderId = useOrderId();

  const { data, isLoading, error } = usePublicDCBE(
    getEnecoBeXapiSiteApiV1OrdersByOrderId,
    {
      path: {
        orderId: orderId ?? '',
      },
    },
    [`/orders/${orderId}`],
    !!orderId,
  );

  const { data: dataContactDetails } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberContactDetails,
    {
      path: {
        accountNumber: selectedAccount.crmAccountNumber,
      },
    },
    [`/accounts/${selectedAccount.crmAccountNumber}/contact/details`],
    session,
  );

  const orderData = unwrapData(data) as GetOrderResponse | null;
  const contactData = unwrapData(dataContactDetails);
  const personalDetails = orderData?.personalDetails ?? contactData;
  const companyDetails = orderData?.companyDetails ?? contactData?.companyData;

  const personalDetailsFields = fields.personalDetailsStep;

  const professionalUserOptions = personalDetailsFields.professionalUserFormField.value.options.map(option => ({
    value: option.name,
    label: option.label,
  }));

  const {
    register,
    setError,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
    control,
  } = useFormContext<OrderManagementFormSchema>();

  const { query } = useRouter();
  const isSoho = query.isSoho === 'true';

  const dateOfBirth = watch('personalDetailsStep.dateOfBirth');
  const isProfessionalUser = watch('personalDetailsStep.professionalUser') === PROFESSIONAL_USER_OPTION_YES;
  const { hasSelectedMaintenance, hasSelectedServiceProposal } = useSelectedProducts({
    orderId,
  });

  const vatStatus = watch('personalDetailsStep.vatStatus');
  const isCocontractor = watch('personalDetailsStep.isCocontractor');
  const receiveInvoicesThroughMail = watch('personalDetailsStep.receiveInvoicesThroughMail');
  const orderPromos = orderData?.promos;
  const hasEBillingPromoLossRisk = (receiveInvoicesThroughMail === false && orderPromos?.[0]?.eBillingFlag) || false;
  const hasSohoPromoLossRisk = (!isProfessionalUser && orderPromos?.[0]?.sohoflag) || false;
  const hasLossOfPromoRisk = hasEBillingPromoLossRisk || hasSohoPromoLossRisk;
  const promoLossRiskMessage = getPromoLossRiskMessage(
    fields,
    isProfessionalUser,
    hasEBillingPromoLossRisk,
    hasSohoPromoLossRisk,
  );

  const defaultValues = useMemo(() => {
    const values: Partial<OrderManagementFormSchema['personalDetailsStep']> = {};

    if (personalDetails) {
      if (personalDetails.firstName) values.firstName = personalDetails.firstName;
      if (personalDetails.lastName) values.lastName = personalDetails.lastName;
      if (personalDetails.dateOfBirth) values.dateOfBirth = new Date(personalDetails.dateOfBirth);
      if (personalDetails.email) values.email = personalDetails.email;
      if ('mobileNumber' in personalDetails) {
        values.phoneNumber = personalDetails.mobileNumber || personalDetails.telephoneNumber || undefined;
      }
      if ('phoneNumber' in personalDetails && values.phoneNumber === undefined) {
        values.phoneNumber = personalDetails.phoneNumber;
      }
      if ('receiveInvoicesThroughMail' in personalDetails && personalDetails.receiveInvoicesThroughMail != null) {
        values.receiveInvoicesThroughMail = personalDetails.receiveInvoicesThroughMail;
      }
    }

    if (companyDetails) {
      values.professionalUser = PROFESSIONAL_USER_OPTION_YES;
      values.companyName =
        ('companyName' in companyDetails ? companyDetails.companyName : undefined) ||
        ('name' in companyDetails ? companyDetails.name : undefined);
      values.companyType =
        ('companyType' in companyDetails ? companyDetails.companyType : undefined) ||
        ('formatType' in companyDetails ? companyDetails.formatType : undefined);
      values.vatStatus = resolveVatStatusFromCompanyDetails({
        companyNumberIsBeingRequested:
          'companyNumberIsBeingRequested' in companyDetails
            ? !!companyDetails.companyNumberIsBeingRequested
            : 'vatType' in companyDetails && companyDetails.vatType === 'CompanyNumberRequested',
        subjectToVAT: 'subjectToVAT' in companyDetails ? companyDetails.subjectToVAT : undefined,
        vatType: 'vatType' in companyDetails ? companyDetails.vatType : undefined,
      });
      if ('vatNumber' in companyDetails && companyDetails.vatNumber) {
        values.VATNumber = companyDetails.vatNumber;
      } else if ('companyNumber' in companyDetails && companyDetails.companyNumber != null) {
        values.VATNumber = String(companyDetails.companyNumber);
      }
      if ('hasCoContracting' in companyDetails && companyDetails.hasCoContracting != null) {
        values.isCocontractor = companyDetails.hasCoContracting;
      }
    } else {
      values.professionalUser = PROFESSIONAL_USER_OPTION_NO;
    }

    return values;
  }, [personalDetails, companyDetails]);

  const initializeDefaultValues = useCallback(() => {
    const currentValues = getValues('personalDetailsStep');
    Object.entries(defaultValues).forEach(([key, value]) => {
      const fieldPath = `personalDetailsStep.${key}` as Path<OrderManagementFormSchema>;
      const currentValue = currentValues?.[key as keyof typeof currentValues];
      if (value !== undefined && !currentValue) {
        setValue(fieldPath, value, { shouldValidate: false });
      }
    });
  }, [defaultValues, getValues, setValue]);

  useEffect(() => {
    initializeDefaultValues();
  }, [initializeDefaultValues]);

  const genderInitialized = useRef(false);
  useEffect(() => {
    if (isLoading || genderInitialized.current) return;
    const orderGender = orderData?.personalDetails?.representation
      ? representationToGender(orderData.personalDetails.representation)
      : undefined;
    const resolvedGender = orderGender ?? contactData?.gender;
    if (resolvedGender) {
      setValue('personalDetailsStep.gender', resolvedGender, { shouldValidate: false });
      genderInitialized.current = true;
    }
  }, [isLoading, orderData, contactData, setValue]);

  useEffect(() => {
    if (isSoho) {
      setValue('personalDetailsStep.professionalUser', PROFESSIONAL_USER_OPTION_YES, { shouldValidate: false });
    }
  }, [isSoho, setValue]);

  useEffect(() => {
    void trigger('personalDetailsStep.VATNumber');
  }, [trigger, vatStatus]);

  useEffect(() => {
    setIsVatRequired(vatStatus === 'VatRequired' || vatStatus === undefined);
  }, [isProfessionalUser, vatStatus]);

  const checkDialogTrigger = () => {
    if (hasLossOfPromoRisk) setShowPromotionWarning(true);
    else void handleOnClickNext();
  };

  const handleConfirmLosePromo = async () => {
    setShowPromotionWarning(false);
    if (orderId) {
      try {
        await deleteEnecoBeXapiSiteApiV1OrdersByOrderIdPromo({
          path: { orderId },
        });
        mutate(`/orders/${orderId}/simulate`);
      } catch (error) {
        logger.error('Pr0m0D', 'Failed to remove promo after user confirmed loss', { error });
      }
    }
    void handleOnClickNext();
  };

  const [hasUpdateError, setHasUpdateError] = useState(false);

  const handleOnClickNext = async () => {
    setShowPromotionWarning(false);
    setHasUpdateError(false);
    if (!orderId) {
      setError('root', { message: 'Order ID is missing' });
      return;
    }

    const values = getValues('personalDetailsStep');
    const isValid = await trigger('personalDetailsStep');
    if (!isValid) {
      return;
    }
    setIsLoadingSubmit(true);

    const representation = genderToRepresentation(values.gender as GenderDto);

    try {
      const body: UpdatePersonalAndCompanyDetailsRequest = {
        isSoho: values.professionalUser === PROFESSIONAL_USER_OPTION_YES,
        personalDetails: {
          representation: representation || representationMr,
          firstName: values.firstName,
          lastName: values.lastName,
          dateOfBirth: values.dateOfBirth ? format(values.dateOfBirth, 'yyyy-MM-dd') : '',
          phoneNumber: values.phoneNumber ?? '',
          email: values.email,
          language: languageToCustomerLanguageDto(language),
          receiveInvoicesThroughMail: values.receiveInvoicesThroughMail ?? null,
        },
        companyDetails:
          values.professionalUser === PROFESSIONAL_USER_OPTION_YES ? buildCompanyDetails(values) : emptyCompanyDetails,
      };

      const result = await putEnecoBeXapiSiteApiV1OrdersByOrderIdPersonalAndCompanyDetails({
        path: {
          orderId: orderId ?? '',
        },
        body,
      });
      const { response, error: resultError } = result;
      unwrapValidationError(resultError);

      if (response.ok) {
        trackEventBE('order_personal_details_submit', { status: 'success' });
        mutate(`/orders/${orderId}/simulate`);
        trackEventBE('be_om_personal_details', {
          status: 'success',
          data: getTrackableFormValues(getValues()),
        });
        nextMainStep();
      } else {
        setHasUpdateError(true);
        trackEventBE('be_om_personal_details', {
          status: 'failed',
          message: 'Failed to update order with personal details',
          data: getTrackableFormValues(getValues()),
        });
      }
    } catch {
      setHasUpdateError(true);
      trackEventBE('be_om_personal_details', {
        status: 'failed',
        message: 'Failed to update order with personal details',
        data: getTrackableFormValues(getValues()),
      });
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  const genderOptions: Array<{ label: string; value: GenderDto }> = [
    {
      label: personalDetailsFields.salutationList.value.options.find(o => o.value === 'dhr')?.label ?? '',
      value: 'Male',
    },
    {
      label: personalDetailsFields.salutationList.value.options.find(o => o.value === 'mevr')?.label ?? '',
      value: 'Female',
    },
  ];

  if (!orderId) {
    return (
      <Bucket title={personalDetailsFields.title.value}>
        <Bucket.Content>
          <NotificationBox
            isAlert
            variant="error"
            title="Error"
            text="Order ID is missing. Please start the order process again."
          />
        </Bucket.Content>
      </Bucket>
    );
  }

  const showDataError = hasUpdateError || (!isLoading && !!error && !data);

  return (
    <Stack gap="10">
      {showDataError && <DataErrorNotification />}
      <Stack gap="2">
        <Heading as="h2" size="M">
          {personalDetailsFields.title.value}
        </Heading>
        <RichText html={personalDetailsFields.subTitle.value} />
      </Stack>
      <Stack gap="6" alignX="start">
        <Bucket
          title={
            <Stack gap={2} direction="row" alignY="center">
              <ProfileIcon />
              <Heading as="h3" size="3XS">
                {personalDetailsFields.title.value}
              </Heading>
            </Stack>
          }>
          <Bucket.Content>
            {genderOptions && (
              <InputSelect
                label={personalDetailsFields.salutationList.value.label}
                error={errors?.personalDetailsStep?.gender?.message}
                hint={<RichText html={personalDetailsFields.salutationList.value.hint} />}
                {...register('personalDetailsStep.gender')}
                options={genderOptions}
                placeholder=""
              />
            )}
            <InputText
              placeholder={personalDetailsFields.firstNameFormField.value.placeholder}
              label={personalDetailsFields.firstNameFormField.value.label}
              hint={<RichText html={personalDetailsFields.firstNameFormField.value.hint} />}
              error={errors?.personalDetailsStep?.firstName?.message}
              {...register('personalDetailsStep.firstName')}
            />
            <InputText
              placeholder={personalDetailsFields.lastNameFormField.value.placeholder}
              label={personalDetailsFields.lastNameFormField.value.label}
              hint={<RichText html={personalDetailsFields.lastNameFormField.value.hint} />}
              error={errors?.personalDetailsStep?.lastName?.message}
              {...register('personalDetailsStep.lastName')}
            />
            <Input
              label={personalDetailsFields.birthDateFormField.value.label}
              id="dateOfBirth"
              type="date"
              hint={<RichText html={personalDetailsFields.birthDateFormField.value.hint} />}
              value={dateOfBirth ? format(dateOfBirth, 'yyyy-MM-dd') : ''}
              error={errors?.personalDetailsStep?.dateOfBirth?.message}
              {...register('personalDetailsStep.dateOfBirth')}
              autoComplete="off"
              max={new Date().toISOString().split('T')[0]}
            />
            <PhoneNumberInputField
              name="personalDetailsStep.phoneNumber"
              label={personalDetailsFields.phoneNumberFormField.value.label}
              hint={<RichText html={personalDetailsFields.phoneNumberFormField.value.hint} />}
              placeholder={personalDetailsFields.phoneNumberFormField.value.placeholder}
            />
            <InputEmail
              label={personalDetailsFields.emailFormField.value.label}
              placeholder={personalDetailsFields.emailFormField.value.placeholder}
              hint={<RichText html={personalDetailsFields.emailFormField.value.hint} />}
              error={errors?.personalDetailsStep?.email?.message}
              autoComplete="email"
              {...register('personalDetailsStep.email')}
            />
            {!isProfessionalUser && (
              <Checkbox
                label={personalDetailsFields.receiveInvoicesThroughMailFormField.value.label}
                defaultChecked={true}
                hint={
                  <Stack direction="row" alignY="center" gap="1">
                    <RichText html={personalDetailsFields.receiveInvoicesThroughMailFormField.value.hint} />
                    <InfoComponent
                      variant="icon"
                      triggerText={personalDetailsFields.receiveInvoicesThroughMailFormField.value.hint}>
                      <RichText html={personalDetailsFields.factureTolltipContent.value} />
                    </InfoComponent>
                  </Stack>
                }
                error={errors?.personalDetailsStep?.receiveInvoicesThroughMail?.message}
                {...register('personalDetailsStep.receiveInvoicesThroughMail')}
              />
            )}
            <AlertDialog
              title={promoLossRiskMessage?.value.title}
              description={<RichText html={promoLossRiskMessage?.value.content} />}
              isOpen={showPromotionWarning}
              setOpen={setShowPromotionWarning}
              confirmText={promoLossRiskMessage?.value.submitButtonText || 'OK [not in sitecore]'}
              onConfirm={handleConfirmLosePromo}
              onDeny={() => {
                setShowPromotionWarning(false);
              }}
              denyText={promoLossRiskMessage?.value.cancelButtonText || 'Cancel [not in sitecore]'}
            />
          </Bucket.Content>
        </Bucket>
        <Heading as="h3" size="S" id="professionalUser">
          {personalDetailsFields.professionalUserFormField.value.label}
        </Heading>
        <Controller
          control={control}
          name="personalDetailsStep.professionalUser"
          render={({ field }) => (
            <RadioGroup
              name={field.name}
              aria-labelledby="professionalUser"
              direction="row"
              value={field.value}
              onValueChange={field.onChange}>
              {professionalUserOptions.map(option => (
                <RadioTile key={option.value} value={option.value}>
                  {option.label}
                </RadioTile>
              ))}
            </RadioGroup>
          )}
        />
        {isProfessionalUser && (
          <Bucket
            title={
              <Stack gap={2} direction="row" alignY="center">
                <ProfileIcon />
                <Heading as="h3" size="3XS">
                  {personalDetailsFields.title.value}
                </Heading>
              </Stack>
            }>
            <Bucket.Content>
              <InputText
                label={personalDetailsFields.companyNameFormField.value.label}
                hint={<RichText html={personalDetailsFields.companyNameFormField.value.hint} />}
                error={errors?.personalDetailsStep?.companyName?.message}
                placeholder={personalDetailsFields.companyNameFormField.value.placeholder}
                {...register('personalDetailsStep.companyName')}
              />
              <InputSelect
                label={
                  personalDetailsFields.companyTypeFRList?.value?.label ??
                  personalDetailsFields.companyTypeList?.value?.label
                }
                hint={
                  <RichText
                    html={
                      language === 'fr'
                        ? (personalDetailsFields.companyTypeFRList?.value?.hint ??
                          personalDetailsFields.companyTypeList?.value?.hint)
                        : personalDetailsFields.companyTypeList?.value?.hint
                    }
                  />
                }
                error={errors?.personalDetailsStep?.companyType?.message}
                {...register('personalDetailsStep.companyType')}
                options={
                  language === 'fr'
                    ? (personalDetailsFields.companyTypeFRList?.value?.options ??
                      personalDetailsFields.companyTypeList?.value?.options)
                    : personalDetailsFields.companyTypeList?.value?.options
                }
                placeholder={
                  language === 'fr'
                    ? (personalDetailsFields.companyTypeFRList?.value?.placeholder ??
                      personalDetailsFields.companyTypeList?.value?.placeholder)
                    : personalDetailsFields.companyTypeList?.value?.placeholder
                }
              />
              <Controller
                control={control}
                name="personalDetailsStep.VATNumber"
                render={({ field: { onChange, name, onBlur, value } }) => (
                  <CompanyNumberInput
                    label={personalDetailsFields.companyNumberFormField.value.label}
                    name={name}
                    placeholder="XXXX.XXX.XXX"
                    error={errors?.personalDetailsStep?.VATNumber?.message}
                    value={value ?? ''}
                    onChange={onChange}
                    onBlur={onBlur}
                  />
                )}
              />
              <InputSelect
                label={personalDetailsFields.vatTypesList?.value?.label ?? ''}
                hint={<RichText html={personalDetailsFields.vatTypesList?.value?.hint ?? ''} />}
                error={errors?.personalDetailsStep?.vatStatus?.message}
                {...register('personalDetailsStep.vatStatus')}
                options={personalDetailsFields.vatTypesList?.value?.options ?? []}
                placeholder={personalDetailsFields.vatTypesList?.value?.placeholder ?? ''}
              />
              {isVatRequired && (hasSelectedMaintenance || hasSelectedServiceProposal) && (
                <Checkbox
                  label={personalDetailsFields.userHasCoContractingCheckboxFormField?.value?.label}
                  hint={personalDetailsFields.userHasCoContractingCheckboxFormField?.value?.hint}
                  isChecked={!!isCocontractor}
                  {...register('personalDetailsStep.isCocontractor')}
                />
              )}

              <NotificationBox
                variant={personalDetailsFields.peppolBillingNotification.value.variant}
                title={personalDetailsFields.peppolBillingNotification.value.title}
                text={<RichText html={personalDetailsFields.peppolBillingNotification.value.content} />}
                isAlert={false}
              />
            </Bucket.Content>
          </Bucket>
        )}
        {errors['root'] && (
          <NotificationBox
            isAlert
            variant="error"
            title="Error"
            text={errors['root']?.message || 'Er ging iets mis bij het opslaan van je gegevens.'}
          />
        )}
        <Button onClick={checkDialogTrigger} isLoading={isLoadingSubmit}>
          {fields.navigation.nextButtonLabel.value}
        </Button>
      </Stack>
    </Stack>
  );
};
